import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Popup,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Recycle,
  Award,
  Users,
  ScanLine,
  Loader2,
  TrendingUp,
  TrendingDown,
  Filter,
  Download,
  ArrowRight,
  Trophy,
  Activity,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAP_CENTER = [37.7431, -0.8523];

const COLORS_TYPE = {
  'Papel/Cartón': '#1E5AA8',
  Vidrio: '#1F8A70',
  Plásticos: '#F59E0B',
  Orgánico: '#84CC16',
  Aceite: '#C2410C',
  Pilas: '#B91C1C',
  Electrónicos: '#7DB9E8',
  Voluminosos: '#6F8AA8',
  Poda: '#22A06B',
};

export default function GestionEcoparqueDashboard() {
  const [stats, setStats] = useState(null);
  const [wasteTypes, setWasteTypes] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    waste_type: '',
    user_code: '',
    date_from: '',
    date_to: '',
  });

  const loadStats = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/ecoparque/stats`);
      if (r.ok) setStats(await r.json());
    } catch (e) {
      console.error(e);
    }
  };

  const loadDeliveries = async () => {
    const params = new URLSearchParams();
    if (filters.waste_type) params.append('waste_type', filters.waste_type);
    if (filters.user_code) params.append('user_code', filters.user_code.trim().toUpperCase());
    if (filters.date_from) params.append('date_from', new Date(filters.date_from).toISOString());
    if (filters.date_to) {
      // Incluir todo el día final
      const dt = new Date(filters.date_to);
      dt.setHours(23, 59, 59, 999);
      params.append('date_to', dt.toISOString());
    }
    params.append('limit', '500');
    try {
      const r = await fetch(`${BACKEND_URL}/api/ecoparque/deliveries?${params.toString()}`);
      if (r.ok) setDeliveries(await r.json());
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.allSettled([
      loadStats(),
      fetch(`${BACKEND_URL}/api/ecoparque/waste-types`)
        .then((r) => (r.ok ? r.json() : []))
        .then((d) => alive && setWasteTypes(Array.isArray(d) ? d : [])),
      loadDeliveries(),
    ]).finally(() => {
      if (alive) setLoading(false);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recargar tabla al cambiar filtros (debounce simple)
  useEffect(() => {
    const id = setTimeout(loadDeliveries, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const exportCSV = () => {
    if (!deliveries.length) {
      toast.error('No hay datos para exportar');
      return;
    }
    const headers = ['ID', 'Fecha', 'Código usuario', 'Nombre', 'Tipo residuo', 'Kg', 'Puntos', 'Lat', 'Lng', 'Notas'];
    const rows = deliveries.map((d) => [
      d.id,
      new Date(d.created_at).toISOString(),
      d.user_code,
      d.user_name || '',
      d.waste_type,
      d.quantity_kg,
      d.points,
      d.lat ?? '',
      d.lng ?? '',
      (d.notes || '').replace(/"/g, '""'),
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c)}"`).join(','))
      .join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    a.download = `ecoparque-entregas-${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exportado CSV (${deliveries.length} filas)`);
  };

  const pieData = useMemo(
    () =>
      (stats?.by_type || []).map((row) => ({
        name: row.waste_type,
        value: row.total_kg,
        entries: row.entries,
      })),
    [stats]
  );

  const evolutionData = useMemo(
    () =>
      (stats?.evolution_30d || []).map((row) => ({
        date: row.date.slice(5), // MM-DD
        kg: row.total_kg,
        entregas: row.entries,
      })),
    [stats]
  );

  const topUsersData = useMemo(
    () =>
      (stats?.top_users || []).map((u) => ({
        name: u.user_name || u.user_code,
        kg: u.total_kg,
        puntos: u.total_points,
      })),
    [stats]
  );

  const deltaKg = stats?.comparison?.delta_kg_pct;
  const deltaEntries = stats?.comparison?.delta_entries_pct;
  const currentKg = stats?.comparison?.current_month?.total_kg ?? 0;
  const prevKg = stats?.comparison?.previous_month?.total_kg ?? 0;

  return (
    <div className="space-y-6" data-testid="gestion-eco-dashboard">
      {/* Header acciones */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-[#0A2540]">Dashboard Ecoparque</h2>
          <p className="text-sm text-[#6F8AA8] mt-1">
            Visión global del Punto Limpio: PAYT, evolución, ciudadanos y orígenes geolocalizados.
          </p>
        </div>
        <Link
          to="/gestion/ecoparque/registrar"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1F8A70] text-white text-sm font-semibold hover:bg-[#177A60] transition-colors shadow-sm"
          data-testid="eco-dash-link-registrar"
        >
          <ScanLine size={16} /> Registrar entrega
          <ArrowRight size={14} />
        </Link>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          icon={Recycle}
          color="#1F8A70"
          label="Total kg reciclados"
          value={loading ? '…' : `${(stats?.total_kg ?? 0).toFixed(1)} kg`}
          testid="eco-dash-kpi-kg"
        />
        <KPICard
          icon={Activity}
          color="#1E5AA8"
          label="Total entregas"
          value={loading ? '…' : stats?.total_entries ?? 0}
          testid="eco-dash-kpi-entries"
        />
        <KPICard
          icon={Users}
          color="#7DB9E8"
          label="Ciudadanos activos"
          value={loading ? '…' : stats?.active_users ?? 0}
          subValue={loading ? null : `${stats?.total_users ?? 0} registrados`}
          testid="eco-dash-kpi-users"
        />
        <KPICard
          icon={Award}
          color="#0A2540"
          label="Puntos PAYT repartidos"
          value={loading ? '…' : (stats?.total_points ?? 0).toLocaleString('es-ES')}
          testid="eco-dash-kpi-points"
        />
      </div>

      {/* Comparativa mensual */}
      <div className="rounded-3xl border border-[#CFE3F5] bg-white p-6">
        <h3 className="font-display text-lg text-[#0A2540] mb-4">Comparativa mensual</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <CompareBox
            title="Mes actual"
            kg={currentKg}
            entries={stats?.comparison?.current_month?.entries ?? 0}
            highlight
          />
          <CompareBox
            title="Mes anterior"
            kg={prevKg}
            entries={stats?.comparison?.previous_month?.entries ?? 0}
          />
          <div className="rounded-2xl border border-[#CFE3F5] bg-[#FAFCFE] p-4 flex flex-col justify-center">
            <p className="text-xs uppercase tracking-wider text-[#6F8AA8] mb-2">Variación</p>
            <DeltaRow label="Kg" value={deltaKg} testid="eco-dash-delta-kg" />
            <DeltaRow label="Entregas" value={deltaEntries} testid="eco-dash-delta-entries" />
          </div>
        </div>
      </div>

      {/* Gráficos: Pie + Línea evolución */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-3xl border border-[#CFE3F5] bg-white p-6">
          <h3 className="font-display text-lg text-[#0A2540] mb-4">Distribución por tipo de residuo</h3>
          {pieData.length === 0 ? (
            <EmptyChart loading={loading} />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(e) => `${e.name}: ${e.value.toFixed(1)}kg`}
                >
                  {pieData.map((entry) => (
                    <Cell key={entry.name} fill={COLORS_TYPE[entry.name] || '#6F8AA8'} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => `${v.toFixed(1)} kg`} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="rounded-3xl border border-[#CFE3F5] bg-white p-6">
          <h3 className="font-display text-lg text-[#0A2540] mb-4">Evolución últimos 30 días</h3>
          {evolutionData.length === 0 ? (
            <EmptyChart loading={loading} />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={evolutionData} margin={{ left: -10, right: 10, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#CFE3F5" />
                <XAxis dataKey="date" stroke="#6F8AA8" fontSize={11} />
                <YAxis stroke="#6F8AA8" fontSize={11} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="kg" stroke="#1F8A70" strokeWidth={2} name="Kg" />
                <Line type="monotone" dataKey="entregas" stroke="#1E5AA8" strokeWidth={2} name="Entregas" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Top 10 + Mapa */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-3xl border border-[#CFE3F5] bg-white p-6">
          <h3 className="font-display text-lg text-[#0A2540] mb-4 flex items-center gap-2">
            <Trophy size={18} className="text-[#F59E0B]" /> Top 10 ciudadanos por kg
          </h3>
          {topUsersData.length === 0 ? (
            <EmptyChart loading={loading} />
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(300, topUsersData.length * 32)}>
              <BarChart data={topUsersData} layout="vertical" margin={{ left: 60, right: 20, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#CFE3F5" />
                <XAxis type="number" stroke="#6F8AA8" fontSize={11} />
                <YAxis type="category" dataKey="name" stroke="#6F8AA8" fontSize={11} width={130} />
                <Tooltip formatter={(v) => `${v.toFixed?.(1) ?? v}`} />
                <Bar dataKey="kg" fill="#1F8A70" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="rounded-3xl border border-[#CFE3F5] bg-white p-6">
          <h3 className="font-display text-lg text-[#0A2540] mb-4">Mapa de orígenes</h3>
          <div className="h-[300px] rounded-2xl overflow-hidden border border-[#CFE3F5]">
            <MapContainer center={MAP_CENTER} zoom={13} style={{ height: '100%', width: '100%' }}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {(stats?.geo_points || []).map((p, i) => (
                <CircleMarker
                  key={`${p.user_code}-${i}`}
                  center={[p.lat, p.lng]}
                  radius={Math.min(4 + Math.sqrt(p.quantity_kg || 1) * 1.5, 18)}
                  pathOptions={{
                    color: COLORS_TYPE[p.waste_type] || '#1E5AA8',
                    fillColor: COLORS_TYPE[p.waste_type] || '#1E5AA8',
                    fillOpacity: 0.55,
                    weight: 1,
                  }}
                >
                  <Popup>
                    <div className="text-xs">
                      <p className="font-semibold text-[#0A2540]">{p.waste_type}</p>
                      <p className="text-[#6F8AA8]">
                        {p.quantity_kg} kg · {p.user_code}
                      </p>
                      <p className="text-[#6F8AA8]">{new Date(p.created_at).toLocaleString('es-ES')}</p>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
          {(stats?.geo_points || []).length === 0 && (
            <p className="text-xs text-[#6F8AA8] mt-3 italic">
              No hay entregas geolocalizadas todavía. Las próximas entregas registradas con ubicación
              aparecerán aquí.
            </p>
          )}
        </div>
      </div>

      {/* Histórico filtrable + CSV */}
      <div className="rounded-3xl border border-[#CFE3F5] bg-white p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-display text-lg text-[#0A2540] flex items-center gap-2">
            <Filter size={18} className="text-[#1E5AA8]" /> Histórico de entregas
          </h3>
          <button
            type="button"
            onClick={exportCSV}
            disabled={!deliveries.length}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1E5AA8] text-white text-xs font-medium hover:bg-[#0F4C97] disabled:opacity-50 transition-colors"
            data-testid="eco-dash-export-csv"
          >
            <Download size={14} /> Exportar CSV
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <FilterField label="Tipo de residuo">
            <select
              value={filters.waste_type}
              onChange={(e) => setFilters((f) => ({ ...f, waste_type: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] text-sm"
              data-testid="eco-dash-filter-type"
            >
              <option value="">Todos</option>
              {wasteTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Código ciudadano">
            <input
              type="text"
              value={filters.user_code}
              onChange={(e) => setFilters((f) => ({ ...f, user_code: e.target.value }))}
              placeholder="ECO-XXXXX"
              className="w-full px-3 py-2 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] text-sm font-mono uppercase"
              data-testid="eco-dash-filter-user"
            />
          </FilterField>
          <FilterField label="Desde">
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] text-sm"
              data-testid="eco-dash-filter-from"
            />
          </FilterField>
          <FilterField label="Hasta">
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] text-sm"
              data-testid="eco-dash-filter-to"
            />
          </FilterField>
        </div>
        {(filters.waste_type || filters.user_code || filters.date_from || filters.date_to) && (
          <button
            type="button"
            onClick={() => setFilters({ waste_type: '', user_code: '', date_from: '', date_to: '' })}
            className="mb-4 inline-flex items-center gap-1 text-xs text-[#6F8AA8] hover:text-[#0A2540]"
          >
            <X size={12} /> Limpiar filtros
          </button>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="eco-dash-deliveries-table">
            <thead>
              <tr className="border-b border-[#CFE3F5]">
                <th className="text-left py-2.5 px-3 font-semibold text-[#0A2540]">Fecha</th>
                <th className="text-left py-2.5 px-3 font-semibold text-[#0A2540]">Ciudadano</th>
                <th className="text-left py-2.5 px-3 font-semibold text-[#0A2540]">Tipo</th>
                <th className="text-right py-2.5 px-3 font-semibold text-[#0A2540]">Kg</th>
                <th className="text-right py-2.5 px-3 font-semibold text-[#0A2540]">Puntos</th>
                <th className="text-left py-2.5 px-3 font-semibold text-[#0A2540]">Geo</th>
              </tr>
            </thead>
            <tbody>
              {deliveries.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-[#6F8AA8]">
                    {loading ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2 size={14} className="animate-spin" /> Cargando…
                      </span>
                    ) : (
                      'Sin entregas para los filtros seleccionados.'
                    )}
                  </td>
                </tr>
              ) : (
                deliveries.map((d) => (
                  <tr key={d.id} className="border-b border-[#CFE3F5] hover:bg-[#FAFCFE]">
                    <td className="py-2.5 px-3 text-[#6F8AA8] tabular-nums">
                      {new Date(d.created_at).toLocaleString('es-ES')}
                    </td>
                    <td className="py-2.5 px-3 text-[#0A2540]">
                      <span className="font-mono text-[12px] text-[#1F8A70]">{d.user_code}</span>
                      {d.user_name && <span className="text-[#6F8AA8] ml-2">· {d.user_name}</span>}
                    </td>
                    <td className="py-2.5 px-3">
                      <span
                        className="inline-block px-2 py-0.5 rounded-full text-[11px] font-medium"
                        style={{
                          background: `${COLORS_TYPE[d.waste_type] || '#6F8AA8'}1f`,
                          color: COLORS_TYPE[d.waste_type] || '#6F8AA8',
                        }}
                      >
                        {d.waste_type}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums text-[#0A2540] font-semibold">
                      {d.quantity_kg.toFixed(1)}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums text-[#1E5AA8]">+{d.points.toFixed(0)}</td>
                    <td className="py-2.5 px-3 text-[#6F8AA8] text-xs">
                      {d.lat != null && d.lng != null ? '📍' : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-[#6F8AA8] mt-3">
          Mostrando {deliveries.length} entrega{deliveries.length !== 1 ? 's' : ''}
          {deliveries.length === 500 ? ' · límite alcanzado' : ''}.
        </p>
      </div>
    </div>
  );
}

function KPICard({ icon: Icon, color, label, value, subValue, testid }) {
  return (
    <div className="rounded-3xl border border-[#CFE3F5] bg-white p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}1f` }}>
          <Icon size={18} style={{ color }} />
        </div>
        <p className="text-[11px] uppercase tracking-wider text-[#6F8AA8]">{label}</p>
      </div>
      <p className="font-display text-3xl tabular-nums" style={{ color }} data-testid={testid}>
        {value}
      </p>
      {subValue && <p className="text-xs text-[#6F8AA8] mt-1">{subValue}</p>}
    </div>
  );
}

function CompareBox({ title, kg, entries, highlight = false }) {
  return (
    <div
      className={`rounded-2xl p-4 border ${
        highlight ? 'bg-[#F0FBF6] border-[#1F8A70]' : 'bg-[#FAFCFE] border-[#CFE3F5]'
      }`}
    >
      <p className="text-xs uppercase tracking-wider text-[#6F8AA8] mb-1">{title}</p>
      <p className="font-display text-2xl text-[#0A2540] tabular-nums">{kg.toFixed(1)} kg</p>
      <p className="text-xs text-[#6F8AA8]">{entries} entrega{entries !== 1 ? 's' : ''}</p>
    </div>
  );
}

function DeltaRow({ label, value, testid }) {
  if (value == null) {
    return (
      <p className="text-sm text-[#6F8AA8]" data-testid={testid}>
        {label}: <span className="font-medium">—</span>
      </p>
    );
  }
  const positive = value >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <p className="text-sm flex items-center gap-1.5" data-testid={testid}>
      <span className="text-[#6F8AA8]">{label}:</span>
      <span
        className="inline-flex items-center gap-1 font-semibold"
        style={{ color: positive ? '#1F8A70' : '#C2410C' }}
      >
        <Icon size={14} />
        {positive ? '+' : ''}
        {value}%
      </span>
    </p>
  );
}

function FilterField({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-medium text-[#0A2540] uppercase tracking-wider mb-1.5 block">
        {label}
      </span>
      {children}
    </label>
  );
}

function EmptyChart({ loading }) {
  return (
    <div className="h-[260px] flex items-center justify-center text-sm text-[#6F8AA8]">
      {loading ? (
        <span className="inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Cargando…
        </span>
      ) : (
        'Sin datos suficientes todavía.'
      )}
    </div>
  );
}
